import React from 'react';
import { createPortal } from 'react-dom';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore'; // THÊM serverTimestamp
import { db } from '../firebase'; // Giả sử bạn có file firebase config

// Hàm để chuyển đổi URL thành liên kết có thể nhấp được
const linkifyText = (text) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
  const result = [];
  let lastIndex = 0;
  let match;
  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(text.substring(lastIndex, match.index));
    }
    let url = match[0];
    // Nếu là www. mà không có http thì thêm http://
    const href = url.startsWith('http') ? url : `http://${url}`;
    result.push(
      <a key={`link-${match.index}`} href={href} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{url}</a>
    );
    lastIndex = match.index + url.length;
  }
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }
  return result;
};

// Hàm lấy biểu tượng và màu sắc dựa trên vai trò
const getRoleIconAndColor = (role) => {
  switch(role) {
    case 'executor':
      return {
        icon: 'fas fa-user-cog',
        color: 'text-emerald-500 dark:text-emerald-400',
        bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
        label: 'Người thực hiện'
      };
    case 'collaborator':
      return {
        icon: 'fas fa-people-arrows',
        color: 'text-blue-500 dark:text-blue-400',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        label: 'Người phối hợp'
      };
    case 'supervisor':
      return {
        icon: 'fas fa-user-shield',
        color: 'text-yellow-500 dark:text-yellow-400',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
        label: 'Người giám sát'
      };
    case 'approver':
      return {
        icon: 'fas fa-user-check',
        color: 'text-purple-500 dark:text-purple-400',
        bgColor: 'bg-purple-100 dark:bg-purple-900/30',
        label: 'Người phê duyệt'
      };
    default:
      return {
        icon: 'fas fa-user',
        color: 'text-gray-500 dark:text-gray-400',
        bgColor: 'bg-gray-100 dark:bg-gray-700',
        label: 'Người tham gia'
      };
  }
};

const TaskDetailsModal = ({ task, teams, onClose, onEdit, onDelete, onUpdateTask }) => {
  const teamName = teams.find(t => t.id === task.teamId)?.name || '—';
  
  // Tổ chức người tham gia theo vai trò
  const organizeParticipants = () => {
    // Sử dụng participants nếu có hoặc sử dụng assignees (để tương thích ngược)
    const allParticipants = task.participants || task.assignees || [];
    
    // Phân loại người tham gia theo vai trò
    const executors = allParticipants.filter(p => p.role === 'executor' || !p.role);
    const collaborators = allParticipants.filter(p => p.role === 'collaborator');
    const supervisors = allParticipants.filter(p => p.role === 'supervisor');
    const approvers = allParticipants.filter(p => p.role === 'approver');
    
    return {
      executors,
      collaborators,
      supervisors,
      approvers,
      hasParticipants: allParticipants.length > 0
    };
  };
  
  const { executors, collaborators, supervisors, approvers, hasParticipants } = organizeParticipants();

  // Hàm xử lý khi người dùng thay đổi trạng thái công việc (ví dụ)
  // Bạn cần tích hợp hàm này vào UI cho phép thay đổi trạng thái,
  // ví dụ: một dropdown chọn trạng thái trong modal này hoặc trong TimeLine.js
  const handleStatusChange = async (newStatus) => {
    if (!task || !task.id) return;
    const taskDocRef = doc(db, 'tasks', task.id);
    const updateData = { status: newStatus };
    if (newStatus === 'completed') {
      updateData.completedAt = serverTimestamp();
    } else {
      // Nếu công việc được chuyển từ 'completed' sang trạng thái khác,
      // có thể bạn muốn xóa completedAt hoặc giữ nguyên tùy theo logic nghiệp vụ
      // updateData.completedAt = null; // hoặc deleteField() nếu muốn xóa hoàn toàn
    }
    try {
      await updateDoc(taskDocRef, updateData);
      if (onUpdateTask) {
        onUpdateTask({ ...task, ...updateData }); // Cập nhật lại state ở component cha nếu cần
      }
      // onClose(); // Có thể đóng modal sau khi cập nhật
    } catch (error) {
      console.error("Error updating task status:", error);
      // Xử lý lỗi (ví dụ: hiển thị thông báo cho người dùng)
    }
  };


  // Hiển thị danh sách người tham gia theo vai trò
  const renderParticipantsByRole = (participants, role) => {
    if (!participants || participants.length === 0) return null;
    
    const { icon, color, bgColor, label } = getRoleIconAndColor(role);
    
    return (
      <div className="mb-2">
        <div className={`flex items-center mb-1 ${color}`}>
          <i className={`${icon} mr-1.5`}></i>
          <span className="font-medium text-sm">{label}</span>
        </div>
        <ul className="space-y-1 pl-6">
          {participants.map((p, index) => (
            <li key={`${role}-${index}`} className="flex items-center text-sm">
              <i className="fas fa-angle-right mr-1.5 text-gray-400 dark:text-gray-500"></i>
              {p.displayName || p.email}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 max-w-lg w-full rounded-lg shadow-lg p-6 animate-fade-in">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
            {task.title || task.text}
          </h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors focus:outline-none"
            aria-label="Đóng"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
          <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p><strong className="text-gray-800 dark:text-gray-200">Phòng ban:</strong> {teamName}</p>
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p>
              <strong className="text-gray-800 dark:text-gray-200">Trạng thái:</strong> 
              <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                task.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 
                task.status === 'inProgress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                task.status === 'notStarted' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                task.status === 'canceled' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
              }`}>
                {task.status === 'completed' ? 'Hoàn thành' : 
                 task.status === 'inProgress' ? 'Đang thực hiện' :
                 task.status === 'notStarted' ? 'Chưa bắt đầu' :
                 task.status === 'canceled' ? 'Đã hủy' : task.status}
              </span>
            </p>
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p>
              <strong className="text-gray-800 dark:text-gray-200">Ưu tiên:</strong> 
              <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                task.priority === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : 
                task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
              }`}>
                {task.priority === 'high' ? 'Cao' : 
                 task.priority === 'medium' ? 'Trung bình' : 'Thấp'}
              </span>
            </p>
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg flex justify-between items-center">
            <p><strong className="text-gray-800 dark:text-gray-200">Ngày bắt đầu:</strong> {new Date(task.start_date).toLocaleDateString()}</p>
            {task.duration && (
              <p><strong className="text-gray-800 dark:text-gray-200">Hạn chót:</strong> {new Date(new Date(task.start_date).getTime() + task.duration*864e5).toLocaleDateString()}</p>
            )}
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p><strong className="text-gray-800 dark:text-gray-200">Tiến độ:</strong></p>
            <div className="mt-2">
              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                <div 
                  className="bg-emerald-500 h-2.5 rounded-full transition-all duration-300" 
                  style={{ width: `${task.progress || 0}%` }}
                ></div>
              </div>
              <p className="text-right mt-1 text-sm text-gray-600 dark:text-gray-400">{task.progress || 0}%</p>
            </div>
          </div>
          
          {/* Phần hiển thị người tham gia theo vai trò */}
          {hasParticipants && (
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p className="mb-2"><strong className="text-gray-800 dark:text-gray-200">Người tham gia:</strong></p>
              <div className="pl-2 border-l-2 border-gray-200 dark:border-gray-600">
                {renderParticipantsByRole(executors, 'executor')}
                {renderParticipantsByRole(collaborators, 'collaborator')}
                {renderParticipantsByRole(supervisors, 'supervisor')}
                {renderParticipantsByRole(approvers, 'approver')}
              </div>
            </div>
          )}
          
          {task.description && (
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p><strong className="text-gray-800 dark:text-gray-200">Mô tả:</strong></p> 
              <p className="mt-1 whitespace-pre-wrap">{linkifyText(task.description)}</p>
            </div>
          )}
        </div>
        <div className="mt-6 flex justify-between">
          {onDelete && (
            <button 
              onClick={onDelete} 
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all duration-200 flex items-center shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Xóa
            </button>
          )}
          <div className="space-x-3">
            {onEdit && (
              <button 
                onClick={onEdit} 
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all duration-200 flex items-center shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Chỉnh sửa
              </button>
            )}
            <button 
              onClick={onClose} 
              className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-all duration-200 flex items-center shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-opacity-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Đóng
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TaskDetailsModal;
